import { computed, getCurrentInstance, type ComputedRef, type Ref } from 'vue'

/**
 * Generate a stable, deterministic-ish ID for field controls.
 *
 * Priority:
 * 1) explicit `id`
 * 2) `name` (prefixed) so it is stable across renders
 * 3) component instance UID fallback
 *
 * `CoreField` uses this to connect `<label for>` and message IDs.
 */
function sanitizeId(value: string) {
  return value
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9\-_:.]/g, '-')
}

export function useStableId(options: {
  id?: Ref<string | undefined>
  name?: Ref<string | undefined>
  prefix?: string
}): ComputedRef<string> {
  const instance = getCurrentInstance()
  const uid = instance?.uid ?? Math.floor(Math.random() * 1_000_000)
  const prefix = options.prefix ?? 'core-field'

  const fallback = `${prefix}-${uid}`

  return computed(() => {
    const providedId = options.id?.value
    if (typeof providedId === 'string' && providedId.trim()) return sanitizeId(providedId)

    const name = options.name?.value
    if (typeof name === 'string' && name.trim()) return `${prefix}-${sanitizeId(name)}`

    return fallback
  })
}
